import React, { useEffect, useMemo, useRef, useState } from "react";
import ListTotalsFooter from "../../components/ListTotalsFooter";
import {
  Box,
  Button,
  Typography,
  TextField,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tabs,
  Tab,
  Tooltip,
  Popover,
} from "@mui/material";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import { FileDownload } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { fitColumns } from "../../utils/excelFit";

import { apiData } from "../../services/api_service";
import { fetchFocRequestsAPI, focRequestBaseAPI } from "../../config";
import Layout from "../../layout/Layout";
import { formatNumber, formatAED } from "../../utils/format";
import { AlertMessage } from "../../components/AlertMessage";
import { writeXlsxFrozen } from "../../utils/xlsxFreeze";

const STATUS_COLOR = {
  pending: "warning",
  approved: "success",
  rejected: "error",
};

const STATUS_ICON = {
  pending: HourglassEmptyRoundedIcon,
  approved: CheckCircleOutlineIcon,
  rejected: HighlightOffIcon,
};

const FocRowActionsMenu = ({ status, onApprove, onReject }) => (
  <Box display="flex" gap={0.75} flexWrap="nowrap" whiteSpace="nowrap">
    <Button
      size="small"
      variant="contained"
      color="success"
      startIcon={<CheckCircleOutlineIcon fontSize="small" />}
      onClick={onApprove}
      sx={{ textTransform: "none", minWidth: 0, px: 1.25, whiteSpace: "nowrap" }}
    >
      {status === "rejected" ? "Approve again" : "Approve"}
    </Button>
    {status === "pending" && (
      <Button
        size="small"
        variant="outlined"
        color="error"
        startIcon={<HighlightOffIcon fontSize="small" />}
        onClick={onReject}
        sx={{ textTransform: "none", minWidth: 0, px: 1.25, whiteSpace: "nowrap" }}
      >
        Reject
      </Button>
    )}
  </Box>
);

const FocApprovals = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const alertRef = useRef();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  // Menu id 12 = "FOC Approval" — only positions granted it (e.g. Chief
  // Executive) can act on a request; everyone else with Invoice access still
  // sees this page (read-only), so sales staff can check where their FOC
  // request stands.
  const allowedMenus = user?.menus
    ? user.menus.split(",").map((id) => parseInt(id.trim(), 10))
    : [];
  const positionName = String(
    user?.position_name || user?.role_name || user?.role || user?.position || ""
  )
    .trim()
    .toLowerCase();
  const isAdminOrChiefExecutive =
    positionName === "admin" || positionName === "chief executive";
  const canApprove = allowedMenus.includes(12) && isAdminOrChiefExecutive;

  // Fetched once (every status, unfiltered) rather than re-querying the
  // server on every tab click — the whole list is small enough that
  // filtering client-side is instant and lets the tabs carry a live count
  // badge for free.
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Lets a dashboard card ("Pending FOC", "Converted FOC", ...) deep-link
  // straight into the right tab via navigate(..., { state: { statusTab } }).
  const [statusTab, setStatusTab] = useState(
    () => location.state?.statusTab || "pending",
  );
  const [actionTarget, setActionTarget] = useState(null); // { row, type: 'approve' | 'reject' }
  const [rejectReason, setRejectReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [decisionPopover, setDecisionPopover] = useState(null); // { anchorEl, row }

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await apiData("get", fetchFocRequestsAPI, { status: "" });
      setAllRows(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      console.error("Failed to fetch FOC requests", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dashboard drill-down scope: date window, store and salesperson (the FOC
  // requester) carried over from the dashboard filters.
  const dashScope = useMemo(() => {
    const st = location.state || {};
    return {
      from: st.fromDate || "",
      to: st.toDate || "",
      storeId: Number(st.storeId) > 0 ? Number(st.storeId) : 0,
      requestedBy: Number(st.salesPersonId) > 0 ? Number(st.salesPersonId) : 0,
    };
  }, [location.state]);

  const scopedRows = useMemo(
    () =>
      allRows.filter((r) => {
        const day = r.requested_at ? dayjs(r.requested_at).format("YYYY-MM-DD") : "";
        if (dashScope.from && day < dashScope.from) return false;
        if (dashScope.to && day > dashScope.to) return false;
        if (dashScope.storeId && Number(r.store_id) !== dashScope.storeId) return false;
        if (dashScope.requestedBy && Number(r.requested_by) !== dashScope.requestedBy) return false;
        return true;
      }),
    [allRows, dashScope]
  );

  const rows = useMemo(
    () => (statusTab === "all" ? scopedRows : scopedRows.filter((r) => r.status === statusTab)),
    [scopedRows, statusTab]
  );

  const tabCounts = useMemo(
    () => ({
      all: scopedRows.length,
      pending: scopedRows.filter((r) => r.status === "pending").length,
      approved: scopedRows.filter((r) => r.status === "approved").length,
      rejected: scopedRows.filter((r) => r.status === "rejected").length,
    }),
    [scopedRows]
  );

  const closeDialog = () => {
    setActionTarget(null);
    setRejectReason("");
  };

  const handleConfirm = async () => {
    if (!actionTarget) return;
    const { row, type } = actionTarget;
    try {
      setSubmitting(true);
      const result =
        type === "approve"
          ? await apiData("post", `${focRequestBaseAPI}/${row.id}/approve`)
          : await apiData("post", `${focRequestBaseAPI}/${row.id}/reject`, {
              reason: rejectReason,
            });

      alertRef.current?.triggerAlert(
        JSON.stringify({
          message: result.msg,
          messageType: result.success ? "success" : "error",
        })
      );
      if (result.success) {
        const nextStatusTab = type === "approve" ? "approved" : "rejected";
        closeDialog();
        setStatusTab(nextStatusTab);
        fetchRequests();
      }
    } catch (error) {
      alertRef.current?.triggerAlert(
        JSON.stringify({
          message: "Something went wrong. Please try again.",
          messageType: "error",
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Exports whatever the table is currently showing — respects the active
  // tab (via `rows`) and any column filters/sort the user has applied.
  const handleExportData = (table) => {
    const visibleRows = table.getFilteredRowModel().rows.map((r) => r.original);
    if (!visibleRows.length) {
      alertRef.current?.triggerAlert(
        JSON.stringify({ message: "No data to export!", messageType: "warning" })
      );
      return;
    }
    // One block per FOC request: the request-level fields are written once (on
    // the request's first item row) and merged down across its item rows, so a
    // request with several free products reads as a single row with the product
    // columns (Product/SKU/Qty/Unit Price/Value) repeating beneath it.
    const exportData = [];
    const merges = [];
    // Column indices (0-based) of the request-level fields to merge vertically.
    const reqCols = [0, 1, 2, 3, 9, 10, 11, 12, 13];
    let rowIdx = 1; // row 0 is the header json_to_sheet writes
    visibleRows.forEach((r) => {
      const items =
        Array.isArray(r.foc_items) && r.foc_items.length ? r.foc_items : [null];
      const startRow = rowIdx;
      items.forEach((it, i) => {
        const first = i === 0;
        exportData.push({
          "Request #": first ? r.request_number || "" : "",
          "Requested On": first && r.requested_at ? dayjs(r.requested_at).format("DD-MM-YYYY") : "",
          "Customer / Company": first ? r.customer_name || "" : "",
          "Created By": first ? r.requested_by_name || "" : "",
          "FOC Product": it ? it.product_name : "",
          SKU: it ? it.sku : "",
          Qty: it ? Number(it.quantity) || 0 : "",
          "Unit Price": formatAED(it ? it.unit_price : 0),
          "FOC Value": formatAED(it ? it.foc_value : 0),
          "Invoice Total": first ? formatAED(r.grand_total || 0) : "",
          Status: first && r.status ? r.status[0].toUpperCase() + r.status.slice(1) : "",
          "Decided By": first ? r.decided_by_name || "" : "",
          "Decided On": first && r.decided_at ? dayjs(r.decided_at).format("DD-MM-YYYY") : "",
          Note: first ? r.decision_note || "" : "",
        });
        rowIdx += 1;
      });
      const endRow = rowIdx - 1;
      if (endRow > startRow) {
        reqCols.forEach((c) =>
          merges.push({ s: { r: startRow, c }, e: { r: endRow, c } })
        );
      }
    });
    // Total footer: qty and FOC value sum over the item rows; Invoice Total
    // sums once per request (not per item, which would double-count).
    const totalQty = visibleRows.reduce(
      (a, r) =>
        a +
        (Array.isArray(r.foc_items)
          ? r.foc_items.reduce((q, it) => q + (Number(it.quantity) || 0), 0)
          : 0),
      0
    );
    exportData.push({
      "Customer / Company": "Total",
      Qty: totalQty,
      "FOC Value": formatAED(visibleRows.reduce((a, r) => a + (Number(r.total_foc_value) || 0), 0)),
      "Invoice Total": formatAED(visibleRows.reduce((a, r) => a + (Number(r.grand_total) || 0), 0)),
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    if (merges.length) ws["!merges"] = merges;
    const wb = XLSX.utils.book_new();
    fitColumns(ws);
    XLSX.utils.book_append_sheet(wb, ws, "FOC Approvals");
    writeXlsxFrozen(XLSX, wb, `FOC_Approvals_${dayjs().format("DD_MM_YYYY")}.xlsx`);
  };

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => {
          acc.count += 1;
          acc.focValue += Number(r.total_foc_value) || 0;
          acc.invoiceTotal += Number(r.grand_total) || 0;
          return acc;
        },
        { count: 0, focValue: 0, invoiceTotal: 0 }
      ),
    [rows]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "request_number",
        header: "Request #",
        size: 150,
        // The request number itself is the "view" action now — one column
        // instead of a number column plus a narrow, easy-to-miss View button
        // next to it.
        Cell: ({ row }) => (
          <Button
            size="small"
            variant="text"
            startIcon={<VisibilityOutlinedIcon fontSize="small" />}
            sx={{ textTransform: "none", fontWeight: 700, px: 0.5 }}
            onClick={() => navigate(`/invoice/view-foc/${row.original.id}`)}
          >
            {row.original.request_number}
          </Button>
        ),
      },
      {
        accessorKey: "requested_at",
        header: "Requested On",
        size: 150,
        Cell: ({ cell }) =>
          cell.getValue() ? dayjs(cell.getValue()).format("DD-MM-YYYY") : "-",
      },
      { accessorKey: "customer_name", header: "Customer / Company", size: 200 },
      { accessorKey: "requested_by_name", header: "Created By", size: 160 },
      {
        accessorKey: "total_foc_items",
        header: "FOC Items",
        size: 300,
        minSize: 220,
        // Show the actual FOC product(s) with quantity and free value, not
        // just the line count — the bare count (e.g. "1") hid which product
        // and how many units were being given away free.
        Cell: ({ row }) => {
          const items = Array.isArray(row.original.foc_items)
            ? row.original.foc_items
            : [];
          if (!items.length) return formatNumber(row.original.total_foc_items);
          return (
            <Box>
              {items.map((it, idx) => (
                <Typography key={idx} sx={{ fontSize: "inherit", lineHeight: 1.45 }}>
                  {it.product_name}
                  {it.sku ? ` (${it.sku})` : ""} &times; {formatNumber(it.quantity)}
                  {" @ "}
                  {formatAED(it.unit_price)}
                  {" = "}
                  {formatAED(it.foc_value)}
                </Typography>
              ))}
            </Box>
          );
        },
      },
      {
        accessorKey: "total_foc_value",
        header: "FOC Value (free)",
        size: 140,
        Cell: ({ cell }) => `${formatAED(cell.getValue())}`,
        muiTableBodyCellProps: { align: "right" },
      },
      {
        // What the customer is actually billed. It isn't FOC Value minus
        // anything — it's the charged (non-FOC) items plus VAT (and
        // shipping), so the breakdown is shown right under it.
        accessorKey: "grand_total",
        header: "Customer Pays",
        size: 170,
        Cell: ({ row }) => {
          const r = row.original;
          const parts = [
            Number(r.charged_items_amount) > 0 && `Items ${formatAED(r.charged_items_amount)}`,
            Number(r.tax_amount) > 0 && `VAT ${formatAED(r.tax_amount)}`,
            Number(r.shipping_amount) > 0 && `Shipping ${formatAED(r.shipping_amount)}`,
          ].filter(Boolean);
          return (
            <Box sx={{ textAlign: "right" }}>
              <Typography sx={{ fontSize: "inherit" }}>{formatAED(r.grand_total)}</Typography>
              <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                {parts.length ? parts.join(" + ") : "Nothing to pay"}
              </Typography>
            </Box>
          );
        },
        muiTableBodyCellProps: { align: "right" },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        // Approved/Rejected carry a decision (who + when + why) — rather
        // than a permanently-visible Note column, that only shows up as a
        // popover on click of the chip itself. Pending has nothing to show.
        Cell: ({ cell, row }) => {
          const status = cell.getValue();
          const hasDecision = status === "approved" || status === "rejected";
          const StatusIcon = STATUS_ICON[status];
          return (
            <Chip
              icon={StatusIcon ? <StatusIcon sx={{ fontSize: 16 }} /> : undefined}
              label={status}
              size="small"
              color={STATUS_COLOR[status] || "default"}
              onClick={
                hasDecision
                  ? (e) => setDecisionPopover({ anchorEl: e.currentTarget, row: row.original })
                  : undefined
              }
              sx={{
                textTransform: "capitalize",
                fontWeight: 700,
                cursor: hasDecision ? "pointer" : "default",
              }}
            />
          );
        },
      },
      // Approved is terminal (the invoice is already created — no
      // "un-approve"), so the whole Actions column is dead weight on that
      // tab — every row would just show "-". Drop it there instead of
      // rendering an empty-looking column.
      ...(canApprove && statusTab !== "approved"
        ? [
            {
              id: "actions",
              header: "Actions",
              // Wide enough for Approve + Reject to sit side by side without
              // wrapping onto a second line — "Approve again" (Rejected tab)
              // is the longest label, so that's the one sizing this.
              size: 230,
              minSize: 210,
              // Pending gets both actions. Rejected can still be Approved
              // later (CE changed their mind / circumstances changed) —
              // re-Reject isn't offered, it's already rejected. "All" can
              // still contain approved rows even though this tab is hidden
              // on the Approved-only view, so keep the guard.
              Cell: ({ row }) => {
                const status = row.original.status;
                if (status === "approved") return "-";
                return (
                  <FocRowActionsMenu
                    status={status}
                    onApprove={() => setActionTarget({ row: row.original, type: "approve" })}
                    onReject={() => setActionTarget({ row: row.original, type: "reject" })}
                  />
                );
              },
            },
          ]
        : []),
    ],
    [canApprove, statusTab]
  );

  return (
    <Layout>
      <Box sx={{ pt: "85px", px: { xs: 2, md: 3.5 }, pb: 4 }}>
        <AlertMessage ref={alertRef} />
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                FOC Approvals
              </Typography>
              <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 0.25 }}>
                <Link underline="hover" color="inherit" href="/dashboard">
                  Dashboard
                </Link>
                <Typography color="text.primary">FOC Approvals</Typography>
              </Breadcrumbs>
              {!canApprove && (
                <Typography variant="caption" color="text.secondary">
                  View only — approving/rejecting requires the Chief Executive's FOC Approval permission.
                </Typography>
              )}
            </Box>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon sx={{ fontSize: "14px !important" }} />}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Back
            </Button>
          </Box>

          <Tabs
            value={statusTab}
            onChange={(e, v) => setStatusTab(v)}
            sx={{ mt: 1 }}
          >
            {[
              { value: "all", label: "All" },
              { value: "pending", label: "Pending" },
              { value: "approved", label: "Approved" },
              { value: "rejected", label: "Rejected" },
            ].map((t) => (
              <Tab
                key={t.value}
                value={t.value}
                label={
                  <Box display="flex" alignItems="center" gap={0.75}>
                    {t.label}
                    <Chip
                      label={tabCounts[t.value]}
                      size="small"
                      color={statusTab === t.value ? "primary" : "default"}
                      sx={{ height: 20, fontSize: 11, fontWeight: 700, minWidth: 24 }}
                    />
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Paper>

        <Box sx={{ mt: 1.5 }}>
          <MaterialReactTable
            columns={columns}
            data={rows}
            state={{ isLoading: loading }}
            enableGlobalFilter={false}
            enableFullScreenToggle={false}
            enableStickyHeader
            enableRowNumbers
            rowNumberMode="original"
            enableColumnPinning
            initialState={{
              sorting: [{ id: "requested_at", desc: true }],
              pagination: { pageIndex: 0, pageSize: 25 },
              columnPinning: {
                left: ["mrt-row-numbers", "request_number"],
                right: ["actions"],
              },
            }}
            renderBottomToolbarCustomActions={({ table }) => {
              // Total over the table's FILTERED rows (all pages, respecting any
              // column filter) — the same set the Excel export totals — so the
              // list footer and the Excel footer always match.
              const fRows = table.getFilteredRowModel().rows.map((r) => r.original);
              const fFocValue = fRows.reduce((a, r) => a + (Number(r.total_foc_value) || 0), 0);
              const fInvoiceTotal = fRows.reduce((a, r) => a + (Number(r.grand_total) || 0), 0);
              return (
                <ListTotalsFooter
                  items={[
                    { label: `${statusTab === "all" ? "Total requests" : `${statusTab[0].toUpperCase()}${statusTab.slice(1)} requests`} (${fRows.length}) - FOC value`, value: `${formatAED(fFocValue)}`, tone: "purple" },
                    { label: "Invoice total", value: `${formatAED(fInvoiceTotal)}` },
                  ]}
                />
              );
            }}
            renderTopToolbarCustomActions={({ table }) => (
              <Tooltip title="Export data to excel sheet" arrow>
                <Button
                  onClick={() => handleExportData(table)}
                  startIcon={<FileDownload />}
                  sx={{ background: "#1a73e829", textTransform: "none" }}
                >
                  Export To Excel
                </Button>
              </Tooltip>
            )}
            muiTablePaperProps={{ variant: "outlined", elevation: 0, sx: { borderRadius: 3, overflow: "hidden" } }}
            muiTableContainerProps={{
              sx: { maxHeight: "calc(100vh - 320px)", minHeight: 400, overflowY: "auto" },
            }}
          />
        </Box>
      </Box>

      <Dialog
        open={!!actionTarget}
        onClose={submitting ? undefined : closeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2.5, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ pb: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <Box display="flex" alignItems="center" gap={1}>
            {actionTarget?.type === "approve" ? (
              <CheckCircleOutlineIcon color="success" />
            ) : (
              <HighlightOffIcon color="error" />
            )}
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {actionTarget?.type === "approve" ? "Approve FOC request" : "Reject FOC request"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {actionTarget?.row?.request_number}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1.5,
              mb: 2.5,
              p: 1.75,
              borderRadius: 2,
              backgroundColor: "#f6f8fa",
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">Customer</Typography>
              <Typography variant="body1" fontWeight={600}>{actionTarget?.row?.customer_name || "-"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Created by</Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <PersonOutlineIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="body1" fontWeight={600}>{actionTarget?.row?.requested_by_name || "-"}</Typography>
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Invoice total</Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <ReceiptLongOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="body1" fontWeight={600}>{formatAED(actionTarget?.row?.grand_total || 0)}</Typography>
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">FOC value</Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Inventory2OutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="body1" fontWeight={600}>{formatAED(actionTarget?.row?.total_foc_value || 0)}</Typography>
              </Box>
            </Box>
          </Box>

          {actionTarget?.type === "approve" ? (
            <>
              <DialogContentText sx={{ mb: 1.25 }}>
                Approving this request will immediately:
              </DialogContentText>
              <Box component="ul" sx={{ mt: 0, mb: 2.5, pl: 2.5, color: "text.secondary" }}>
                <li>Create the real invoice.</li>
                <li>Deduct {actionTarget?.row?.total_foc_items || 0} FOC item(s) from stock.</li>
              </Box>
              <Box sx={{ display: "flex", gap: 1, p: 1.5, borderRadius: 1.5, backgroundColor: "#fff8e1", color: "#7a5600" }}>
                <WarningAmberRoundedIcon fontSize="small" />
                <Typography variant="body2" fontWeight={600}>
                  This action cannot be undone from this screen.
                </Typography>
              </Box>
            </>
          ) : (
            <DialogContentText sx={{ mb: 2 }}>
              No invoice will be created. This request can still be approved later if needed.
            </DialogContentText>
          )}
          {actionTarget?.type === "reject" && (
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Reason for rejection"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={actionTarget?.type === "approve" ? "success" : "error"}
            onClick={handleConfirm}
            disabled={submitting || (actionTarget?.type === "reject" && !rejectReason.trim())}
            sx={{ textTransform: "none" }}
          >
            {submitting
              ? "Please wait..."
              : actionTarget?.type === "approve"
              ? "Approve"
              : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={!!decisionPopover}
        anchorEl={decisionPopover?.anchorEl}
        onClose={() => setDecisionPopover(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: "capitalize", mb: 1 }}>
            {decisionPopover?.row?.status}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {decisionPopover?.row?.status === "rejected" ? "Rejected by" : "Approved by"}:{" "}
            <b>{decisionPopover?.row?.decided_by_name || "—"}</b>
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: decisionPopover?.row?.status === "approved" || decisionPopover?.row?.status === "rejected" ? 0.5 : 0 }}
          >
            On:{" "}
            {decisionPopover?.row?.decided_at
              ? dayjs(decisionPopover.row.decided_at).format("DD-MM-YYYY")
              : "—"}
          </Typography>
          {decisionPopover?.row?.status === "approved" && (
            <Typography variant="body2" sx={{ mb: decisionPopover?.row?.decision_note ? 0.5 : 0 }}>
              Invoice no.:{" "}
              {decisionPopover?.row?.invoice_number ? (
                <Link
                  component="a"
                  href={decisionPopover.row.invoice_pdf_url || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ fontWeight: 700, verticalAlign: "baseline" }}
                >
                  {decisionPopover.row.invoice_number}
                </Link>
              ) : (
                <b>—</b>
              )}
            </Typography>
          )}
          {decisionPopover?.row?.status === "rejected" && (
            <Typography variant="body2">
              Reason: {decisionPopover?.row?.decision_note || "No reason given"}
            </Typography>
          )}
        </Box>
      </Popover>
    </Layout>
  );
};

export default FocApprovals;
